import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { CapturedDeliveryLocation, DeliveryLocationError, DeliveryLocationIssue } from '../../services/orders/delivery-location.service';
import './delivery-location.css';

interface Props {
  issue?: DeliveryLocationIssue;
  reading?: CapturedDeliveryLocation;
  locating: boolean;
  disabled: boolean;
  onCheck: () => void;
}
export default function DeliveryLocationPanel({ issue, reading, locating, disabled, onCheck }: Props) {
  const [permission, setPermission] = useState<PermissionState | 'unknown'>('unknown');
  const [help, setHelp] = useState(false);
  const [platform, setPlatform] = useState(/iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'iphone' : 'android');
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    let disposed = false, version = 0;
    let listening: PermissionStatus | undefined;
    const changed = () => { if (!disposed && listening) setPermission(listening.state); };
    const check = async () => {
      const current = ++version;
      setNow(Date.now());
      if (!navigator.permissions?.query) return;
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        if (disposed || current !== version) return;
        listening?.removeEventListener('change', changed);
        listening = result; setPermission(result.state);
        listening.addEventListener('change', changed);
      } catch { /* Some mobile browsers do not expose a query; the user can still request location. */ }
    };
    const visible = () => { if (document.visibilityState === 'visible') void check(); };
    void check();
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', visible);
    const timer = window.setInterval(() => setNow(Date.now()), 15000);
    return () => {
      disposed = true; version++;
      listening?.removeEventListener('change', changed);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', visible);
      window.clearInterval(timer);
    };
  }, []);
  useEffect(() => { if (issue === 'PermissionDenied') setHelp(true); }, [issue]);
  const unsupported = !window.isSecureContext ? 'InsecureContext' : !navigator.geolocation ? 'Unsupported' : undefined;
  const problem = unsupported ?? issue;
  const fresh = reading && now - Date.parse(reading.capturedAtUtc) <= 120000;
  const blocked = permission === 'denied' || problem === 'PermissionDenied';
  const title = locating ? 'Getting your location…' : problem ? (blocked ? 'Location blocked' : 'Location needed') :
    blocked ? 'Location blocked' : fresh ? 'Location ready' : permission === 'granted' ? 'Location permission enabled' : 'Location permission needed';
  return <section className="delivery-location" aria-label="Delivery location">
    <div className="delivery-location__heading"><i className={`pi ${blocked ? 'pi-lock' : 'pi-map-marker'}`} aria-hidden="true" />
      <strong role="status" aria-live="polite">{title}</strong><span className="delivery-location__required">Required for Start & End</span></div>
    {problem ? <p role="alert">{new DeliveryLocationError(problem).message}</p> :
      <p>{locating ? 'Choose Allow in the browser prompt and keep this page open while your phone finds its location.' : blocked ?
        'Allow location in website and phone settings, then check again.' : fresh ?
          `Location received · Accuracy ±${Math.round(reading.accuracyMeters)} m. Start/End will capture a new reading.` :
          'Your current location is saved with each Start and End Delivery. Enable access before continuing.'}</p>}
    <div className="delivery-location__actions">
      <Button label={blocked || problem || permission === 'granted' || fresh ? 'Check again' : 'Enable location'}
        icon="pi pi-map-marker" loading={locating} disabled={disabled || !!unsupported} onClick={onCheck} />
      <Button label="How to enable" icon="pi pi-question-circle" outlined onClick={() => setHelp(true)} />
    </div>
    <Dialog header="Enable your phone location" visible={help} onHide={() => setHelp(false)}
      style={{ width: '34rem', maxWidth: '95vw' }} modal className="delivery-location-help"
      footer={<Button label="Done" onClick={() => setHelp(false)} />}>
      <p>Location is required to record the start and end of delivery. Your browser and phone must both allow access.</p>
      <label htmlFor="delivery-location-platform">Your phone</label>
      <select id="delivery-location-platform" className="p-inputtext w-full mt-2" value={platform} onChange={e => setPlatform(e.target.value)}>
        <option value="android">Android — Chrome / Edge</option><option value="iphone">iPhone / iPad — Safari</option>
      </select>
      {platform === 'android' ? <ol>
        <li>Turn on <strong>Location</strong> in your phone settings.</li>
        <li>Open <strong>Settings → Apps → Chrome or Edge → Permissions → Location</strong>. Allow access while using the app. Enable precise location if available.</li>
        <li>Return to Farm2Me. Tap the site information icon beside the website address, then <strong>Permissions → Location → Allow</strong>. You can also find the site under browser Settings → Site permissions/settings → Location.</li>
        <li>Return here, close this help and tap <strong>Check again</strong>. Choose <strong>Allow</strong> if prompted.</li>
      </ol> : <ol>
        <li>Open <strong>Settings → Privacy & Security → Location Services</strong> and turn it on.</li>
        <li>Under <strong>Safari Websites</strong>, allow location while using the app and enable <strong>Precise Location</strong> if available.</li>
        <li>In Safari on Farm2Me, open the page menu beside the address → Website Settings → <strong>Location → Allow</strong>.</li>
        <li>Return here, close this help and tap <strong>Check again</strong>. If you use another browser, also allow location for that browser in phone settings.</li>
      </ol>}
      <p className="text-sm">Menu names vary by phone and browser. If opened inside WhatsApp or another app, open this page in Chrome, Edge or Safari. This website cannot change your phone settings automatically.</p>
    </Dialog>
  </section>;
}
